import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTables, updateTable, joinTables, unjoinTable } from '../../services/TableService';
import { getProducts } from '../../services/ProductService';
import { getCategories } from '../../services/CategoryService';
import { getFlavors } from '../../services/FlavorService';
import {
  getOrder,
  getOrdersByTable,
  addItemsToOrder,
  createOrder,
  createTakeawayOrder,
  sendPendingToKitchen,
  splitOrder,
  updateOrderFulfillment,
  updateItemFulfillmentOverride,
  updateOrderItemNotes,
  removeOrderItem,
  updateOrderTable,
} from '../../services/OrderService';
import { OfflineQueueService } from '../../services/OfflineQueueService';
import { useAuth } from '../../context/AuthContext';
import { useTakeawayOrders } from '../../hooks/useTakeawayOrders';
import { TAKEAWAY_ORDERS_QUERY_KEY } from '../../hooks/orders';
import { usePosTransactions } from './hooks/usePosTransactions';
import { computeOrderTotals } from '../../utils/compute-order-totals';
import { buildReceiptData } from '../../utils/receipt';
import { safeArray } from '../../utils/safe';
import { trackProductUse, getFrequencyMap } from '../../utils/frequentProducts';
import { FREQUENT_CATEGORY_ID } from '../../utils/posConstants';
import { useCancelOrder } from '../../features/orders/hooks/useCancelOrder';
import type { OrderTicketData } from '../../components/Receipt/OrderTicket';
import type { Table } from '../../types/@types/table';
import type { Product } from '../../types/@types/product';
import type { Order, FulfillmentType, ServeMode, LinePriceCalc } from '../../types/@types/order';
import { echo } from '../../lib/echo';

declare global {
  interface Window {
    Pusher: any;
  }
}

export interface CartItem {
  product: Product;
  qty: number;
  flavors?: { id: string; name: string; qty: number }[];
  additions?: Record<string, number>;
  exceptions?: string[];
  linePriceCalc?: LinePriceCalc;
  kitchenSummary?: string;
  serveMode: ServeMode;
  note?: string;
}

type FlavorFlowTarget = 'cart' | 'order';

interface PosContextValue {
  // Data
  tables: Table[] | undefined;
  products: Product[] | undefined;
  categories: any[] | undefined;
  ordersByTable: Order[] | undefined;
  sortedTableOrders: Order[];
  activeOrder: Order | null;
  loadingTables: boolean;
  loadingProducts: boolean;

  // Selection State
  selectedTable: Table | null;
  setSelectedTable: (table: Table | null) => void;
  activeCategory: string | null;
  setActiveCategory: (id: string | null) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isTakeawayMode: boolean;

  // CartState
  cart: Record<string, CartItem>;
  setCart: React.Dispatch<React.SetStateAction<Record<string, CartItem>>>;
  activeOrderId: string | number | null;
  setActiveOrderId: (id: string | number | null) => void;
  draftFulfillment: FulfillmentType;
  setDraftFulfillment: (f: FulfillmentType) => void;
  orderCustomer: { customerName: string; customerPhone: string; customerAddress: string };
  setOrderCustomer: React.Dispatch<React.SetStateAction<{ customerName: string; customerPhone: string; customerAddress: string }>>;

  // UI Flow State
  flavorContext: { product: Product; target: FlavorFlowTarget } | null;
  setFlavorContext: (ctx: { product: Product; target: FlavorFlowTarget } | null) => void;
  optionsKey: string | null;
  setOptionsKey: (key: string | null) => void;
  mobileTab: 'MENU' | 'ORDER';
  setMobileTab: (tab: 'MENU' | 'ORDER') => void;

  // Visual feedback
  pendingProductId: string | null;

  // Actions
  handleProductClick: (product: Product) => void;
  addProductToCart: (product: Product, flavors?: any[], opts?: any) => void;
  addProductToActiveOrder: (product: Product, payload?: any) => void;
  handleOrder: () => void;
  handleSendPending: () => void;
  handleOrderFulfillmentChange: (next: FulfillmentType) => Promise<void>;
  handleActiveItemFulfillment: (itemId: string, next: FulfillmentType | null) => Promise<void>;
  handleUpdateActiveItemNote: (itemId: string, note: string) => Promise<void>;
  handleCartItemServeMode: (key: string, next: ServeMode) => void;
  changeQty: (key: string, delta: number) => void;
  removeCartItem: (key: string) => void;

  // Mutations
  addItemsMutation: any;
  sendToKitchenMutation: any;
  splitOrderMutation: any;
  joinTablesMutation: any;
  unjoinTableMutation: any;
  createOrderMutation: any;
  cancelOrderMutation: any;
  transferOrderTableMutation: any;

  // Derived
  filteredProducts: Product[];
  activeOrderItems: any[];
  hasActiveOrder: boolean;
  orderLocked: boolean;
  hasDraft: boolean;
  totals: { subtotal: number; total: number } | null;
  highestKnownSequence: number;
  nextOrderSequence: number;
  activeOrderTotal: number;
  activeOrderTotals: any;
  refetchActiveOrder: () => void;
  previousOrdersWithItems: any[];
  activeOrderIdMaybeLoading: boolean;

  // Transaction State (from usePosTransactions)
  cashSession: any;
  showOpenCash: boolean;
  setShowOpenCash: (v: boolean) => void;
  showCloseCash: boolean;
  setShowCloseCash: (v: boolean) => void;
  showWithdrawCash: boolean;
  setShowWithdrawCash: (v: boolean) => void;
  withdrawingCash: boolean;
  handleWithdrawCashClick: () => void;
  handleWithdrawCashConfirm: (
    amount: number,
    reason: string,
    kind?: string,
    reference?: string,
    openDrawer?: boolean
  ) => Promise<void> | void;
  showPayment: boolean;
  setShowPayment: (v: boolean) => void;
  processingPayment: boolean;
  receiptToPrint: any;
  setReceiptToPrint: (v: any) => void;
  orderTicketToPrint: OrderTicketData | null;
  setOrderTicketToPrint: (v: OrderTicketData | null) => void;
  printOrderTicket: () => void;
  printPreReceipt: (targetOrder?: Order | null, targetItems?: any[]) => void;
  lastReceipt: any;
  openingCash: boolean;
  closingCash: boolean;
  closeCashSummary: any;
  handlePayClick: () => void;
  handleCancelActiveOrder: () => void;
  removeActiveOrderItem: (itemId: string | number) => void;
  removingItem: boolean;
  handleOpenSessionConfirm: (amount: number) => void;
  handleCloseSessionClick: () => void;
  handleCloseSessionConfirm: (
    finalAmount: number,
    obs: string,
    cashCount: Record<string, number>,
    paymentBreakdown: any[]
  ) => void;
  handleConfirmPayment: (payments: any[]) => void;

  // Helpers
  activeOrderSequence: number | null;
  activeOrderAccountIndex: number | null;
  hasFlavorErrors: boolean;
  hasPendingItems: boolean;
  sendToKitchenMutationIsPending: boolean;
  pendingItemsCount: number;
  splittingOrder: boolean;
  cancelOrderIsPending: boolean;
  createOrderIsPending: boolean;
  buildDetailContentFromOrderItem: (item: any) => React.ReactNode;
  buildDetailContentFromCartItem: (item: CartItem) => React.ReactNode;
}

const PosContext = createContext<PosContextValue | undefined>(undefined);

const TAKEAWAY_TABLE: Table = {
  id: '__takeaway__',
  number: 0,
  capacity: 0,
  status: 'Disponible',
};

export function PosProvider({ mode, children }: { mode: 'TABLE' | 'TAKEAWAY', children: React.ReactNode }) {
  const isTakeawayMode = mode === 'TAKEAWAY';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cancelOrderMutation = useCancelOrder();

  // --- BASIC STATE ---
  const [selectedTable, setSelectedTableInternal] = useState<Table | null>(isTakeawayMode ? TAKEAWAY_TABLE : null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderTicketToPrint, setOrderTicketToPrint] = useState<OrderTicketData | null>(null);
  const [cartByTable, setCartByTable] = useState<Record<string, Record<string, CartItem>>>(() => {
    try {
      const saved = localStorage.getItem('dufiwaiter_cart_by_table');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error('Error loading cart from localStorage', e);
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('dufiwaiter_cart_by_table', JSON.stringify(cartByTable));
    } catch (e) {
      console.error('Error saving cart to localStorage', e);
    }
  }, [cartByTable]);

  const [activeOrderId, setActiveOrderId] = useState<string | number | null>(null);
  const [draftFulfillment, setDraftFulfillment] = useState<FulfillmentType>(isTakeawayMode ? 'TAKEOUT' : 'DINE_IN');
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);
  const [orderCustomer, setOrderCustomer] = useState({ customerName: '', customerPhone: '', customerAddress: '' });
  const [flavorContext, setFlavorContext] = useState<{ product: Product; target: FlavorFlowTarget } | null>(null);
  const [optionsKey, setOptionsKey] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<'MENU' | 'ORDER'>('MENU');
  const [showCancelModal, setShowCancelModal] = useState(false);

  // --- QUERIES ---
  const { data: tables, isLoading: loadingTables, refetch: refetchTables } = useQuery({
    queryKey: ['posTables'],
    queryFn: getTables,
    enabled: !isTakeawayMode,
    staleTime: 10_000,
  });

  const { data: products, isLoading: loadingProducts } = useQuery({
    queryKey: ['posProducts'],
    queryFn: getProducts,
    enabled: isTakeawayMode || !!selectedTable,
    staleTime: 5 * 60 * 1000,
  });

  const { data: categories } = useQuery({
    queryKey: ['posCategories'],
    queryFn: getCategories,
    enabled: isTakeawayMode || !!selectedTable,
    staleTime: 5 * 60 * 1000,
  });

  // Precargar sabores para que el modal de helados abra de inmediato sin latencia
  const { data: allFlavors } = useQuery({
    queryKey: ['flavors'],
    queryFn: getFlavors,
    staleTime: 10 * 60 * 1000,
  });

  const { data: ordersByTable, refetch: refetchOrdersByTable } = useQuery({
    queryKey: ['ordersByTable', selectedTable?.id],
    queryFn: async () => {
      const orders = await getOrdersByTable(selectedTable!.id);
      if (Array.isArray(orders)) {
        // Sembrar la caché de activeOrder de inmediato para evitar la petición secundaria redundante
        orders.forEach(ord => {
          if (ord?.id) {
            queryClient.setQueryData(['activeOrder', ord.id], ord);
            queryClient.setQueryData(['activeOrder', String(ord.id)], ord);
            queryClient.setQueryData(['activeOrder', Number(ord.id)], ord);
          }
        });
      }
      return orders;
    },
    enabled: !isTakeawayMode && !!selectedTable?.id,
    staleTime: 15_000,
  });

  const { data: activeOrder, refetch: refetchActiveOrder, error: activeOrderError } = useQuery({
    queryKey: ['activeOrder', activeOrderId],
    queryFn: () => (activeOrderId ? getOrder(activeOrderId) : null),
    enabled: !!activeOrderId,
    staleTime: 30_000,
    initialData: () => {
      if (!activeOrderId) return undefined;
      return queryClient.getQueryData<Order>(['activeOrder', activeOrderId])
        || queryClient.getQueryData<Order>(['activeOrder', String(activeOrderId)])
        || queryClient.getQueryData<Order>(['activeOrder', Number(activeOrderId)]);
    },
  });

  // --- HELPERS ---
  const formatAdditions = useCallback((raw: any) => {
    if (!raw) return undefined;
    if (Array.isArray(raw)) {
      return raw.map((entry: any) => {
        if (typeof entry === 'string') return entry;
        const qty = entry?.quantity ?? entry?.qty;
        const label = entry?.name ?? entry?.label ?? entry?.title;
        if (label) return qty && qty > 1 ? `${qty}x ${label}` : label;
        return String(entry);
      });
    }
    return [String(raw)];
  }, []);

  const mapOrderItems = useCallback((order?: Order | null) => {
    const list = ((order?.items ?? (order as any)?.products) ?? []) as any[];
    return list.map((item: any) => {
      const productDetails = typeof item.product === 'string' ? products?.find((p) => p.id === item.product) : item.product;
      const base_price = Number(item.basePrice ?? item.base_price ?? (productDetails as Product | undefined)?.price ?? 0);
      const line_subtotal = Number(item.lineSubtotal ?? item.line_subtotal ?? (typeof item.quantity === 'number' ? item.quantity * base_price : 0));
      const unitPrice = item.quantity > 0 ? line_subtotal / item.quantity : base_price;
      return {
        ...item,
        product: productDetails,
        name: item.name || productDetails?.name,
        base_price, line_subtotal, unitPrice,
        formattedAdditions: formatAdditions(item.itemAdditions || item.additions),
        exceptions: Array.isArray(item.itemExceptions) 
          ? item.itemExceptions.map((e: any) => e.name || e.key) 
          : item.exceptions,
        status: (item.status || item.itemStatus || '').toUpperCase()
      };
    });
  }, [products, formatAdditions]);

  // --- CART MANAGEMENT ---
  const cart = useMemo(() => (selectedTable ? cartByTable[String(selectedTable.id)] || {} : {}), [selectedTable, cartByTable]);

  const internalSetCart = useCallback((val: any) => {
    if (!selectedTable) return;
    setCartByTable(prev => ({
      ...prev,
      [String(selectedTable.id)]: typeof val === 'function' ? val(prev[String(selectedTable.id)] || {}) : val
    }));
  }, [selectedTable]);

  const changeQty = useCallback((key: string, delta: number) => {
    internalSetCart((prev: any) => {
      const item = prev[key];
      if (!item) return prev;
      const newQty = item.qty + delta;
      if (newQty <= 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: { ...item, qty: newQty } };
    });
  }, [internalSetCart]);

  const removeCartItem = useCallback((key: string) => {
    internalSetCart((prev: any) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, [internalSetCart]);

  const selectedTableRef = useRef(selectedTable);
  useEffect(() => {
    selectedTableRef.current = selectedTable;
  }, [selectedTable]);

  // --- TABLA SWITCH — reset activeOrderId y searchTerm al cambiar de mesa ---
  const handleSetSelectedTable = useCallback((table: Table | null) => {
    if (table?.id !== selectedTable?.id) {
      setActiveOrderId(null);
      setSearchTerm('');
    }
    setSelectedTableInternal(table);
  }, [selectedTable?.id]);

  // --- REAL-TIME UPDATES ---
  useEffect(() => {
    // Escuchamos actualizaciones de mesas (liberación, unión, cambio de estado, etc)
    const tableChannel = echo.channel('pos-updates');

    tableChannel.listen('TableUpdated', (e: any) => {
      console.log('Echo: Mesa actualizada', e);
      const targetTableId = e?.table?.id ?? e?.tableId;
      queryClient.invalidateQueries({ queryKey: ['posTables'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });

      if (targetTableId) {
        queryClient.invalidateQueries({ queryKey: ['ordersByTable', targetTableId] });
        queryClient.invalidateQueries({ queryKey: ['ordersByTable', String(targetTableId)] });
        queryClient.invalidateQueries({ queryKey: ['ordersByTable', Number(targetTableId)] });
      }

      if (selectedTableRef.current?.id && targetTableId && String(selectedTableRef.current.id) === String(targetTableId)) {
        refetchActiveOrder();
        refetchOrdersByTable();
      }
    });

    // Escuchamos actualizaciones de pedidos (nuevos pedidos desde app móvil, cocina, cobros, etc)
    const orderChannel = echo.channel('orders');

    const handleOrderEvent = (type: string, e: any) => {
      console.log(`Echo: ${type}`, e);
      queryClient.invalidateQueries({ queryKey: ['posTables'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: TAKEAWAY_ORDERS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['takeaway', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });

      const tableId = e?.order?.table_id ?? e?.order?.tableId;
      if (tableId) {
        queryClient.invalidateQueries({ queryKey: ['ordersByTable', tableId] });
        queryClient.invalidateQueries({ queryKey: ['ordersByTable', String(tableId)] });
        queryClient.invalidateQueries({ queryKey: ['ordersByTable', Number(tableId)] });
      }

      if (e?.order?.id) {
        queryClient.invalidateQueries({ queryKey: ['activeOrder', e.order.id] });
        queryClient.invalidateQueries({ queryKey: ['activeOrder', String(e.order.id)] });
      }

      if (selectedTableRef.current?.id && tableId && String(selectedTableRef.current.id) === String(tableId)) {
        refetchActiveOrder();
        refetchOrdersByTable();
      }
    };

    orderChannel.listen('.orders.updated', (e: any) => handleOrderEvent('Pedido actualizado', e));
    orderChannel.listen('.orders.new', (e: any) => handleOrderEvent('Pedido nuevo', e));

    return () => {
      echo.leave('pos-updates');
      echo.leave('orders');
    };
  }, [refetchTables, refetchActiveOrder, refetchOrdersByTable, queryClient]);


  // --- MUTATIONS ---
  const createOrderMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!navigator.onLine) {
        const offlineId = `offline-${Date.now()}`;
        const opType = data.destination === 'TAKEAWAY' ? 'CREATE_TAKEAWAY_ORDER' : 'CREATE_ORDER';
        await OfflineQueueService.pushOperation(opType, data);
        const offlineOrder = {
          id: offlineId,
          ...data,
          status: 'WAITING',
          createdAt: new Date().toISOString(),
          isOffline: true,
          items: (data.items || []).map((i: any, idx: number) => ({
            id: `item-offline-${idx}-${Date.now()}`,
            quantity: i.quantity,
            notes: i.notes,
            serveMode: i.serveMode,
            status: 'PENDING',
          })),
        };
        return { data: offlineOrder, isOffline: true };
      }
      return createOrder(data);
    },
    onSuccess: (response: any) => {
      const data = response.data || response;
      const isOfflineOrder = response.isOffline || String(data.id).startsWith('offline-');

      if (isOfflineOrder) {
        toast.info('💾 Pedido guardado localmente (Sin conexión). Se sincronizará automáticamente.');
      } else {
        toast.success('Pedido creado con éxito');
      }

      // 1. Alimentar la caché de activeOrder al instante (evita espera de red)
      queryClient.setQueryData(['activeOrder', data.id], data);
      queryClient.setQueryData(['activeOrder', String(data.id)], data);

      // 2. CRÍTICO: insertar la nueva orden en la caché de ordersByTable ANTES de
      //    setActiveOrderId, para que el efecto de auto-selección la encuentre
      if (!isTakeawayMode && selectedTable?.id) {
        const tableKey = ['ordersByTable', selectedTable.id];
        const current = queryClient.getQueryData<any[]>(tableKey) ?? [];
        if (!current.find((o: any) => String(o.id) === String(data.id))) {
          queryClient.setQueryData(tableKey, [...current, data]);
        }
      }

      setActiveOrderId(data.id);
      internalSetCart({});
      refetchTables();
      // Refrescar ordersByTable
      queryClient.invalidateQueries({ queryKey: ['ordersByTable', selectedTable?.id] });
      if (isTakeawayMode) {
        queryClient.setQueryData<any[]>(TAKEAWAY_ORDERS_QUERY_KEY, (current = []) => {
          if (current.find((o: any) => String(o.id) === String(data.id))) return current;
          return [data, ...current];
        });
        queryClient.invalidateQueries({ queryKey: TAKEAWAY_ORDERS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: ['takeaway', 'list'] });
        navigate('/pos');
      }
    },
    onError: async (error: any, variables: any) => {
      const isNetworkErr = !error.response || error.code === 'ERR_NETWORK' || error.message?.includes('Network');
      if (isNetworkErr && variables) {
        const offlineId = `offline-${Date.now()}`;
        const opType = variables.destination === 'TAKEAWAY' ? 'CREATE_TAKEAWAY_ORDER' : 'CREATE_ORDER';
        await OfflineQueueService.pushOperation(opType, variables);
        const offlineOrder = {
          id: offlineId,
          ...variables,
          status: 'WAITING',
          createdAt: new Date().toISOString(),
          isOffline: true,
          items: (variables.items || []).map((i: any, idx: number) => ({
            id: `item-offline-${idx}-${Date.now()}`,
            quantity: i.quantity,
            notes: i.notes,
            serveMode: i.serveMode,
            status: 'PENDING',
          })),
        };
        toast.warn('⚠️ Sin conexión. Pedido guardado en memoria local y listo para sincronizar.');
        queryClient.setQueryData(['activeOrder', offlineId], offlineOrder);
        queryClient.setQueryData(['activeOrder', String(offlineId)], offlineOrder);
        if (!isTakeawayMode && selectedTable?.id) {
          const tableKey = ['ordersByTable', selectedTable.id];
          const current = queryClient.getQueryData<any[]>(tableKey) ?? [];
          queryClient.setQueryData(tableKey, [...current, offlineOrder]);
        }
        setActiveOrderId(offlineId);
        internalSetCart({});
        return;
      }
      const msg = error?.response?.data?.message || error?.message || 'Error desconocido';
      toast.error(`Error al crear el pedido: ${msg}`);
      console.error('Create order error:', error?.response?.data || error);
    }
  });

  const addItemsMutation = useMutation({
    mutationFn: async ({ orderId, items }: { orderId: string | number, items: any[] }) => {
      if (!navigator.onLine || String(orderId).startsWith('offline-')) {
        await OfflineQueueService.pushOperation('ADD_ITEMS', { id: orderId, items });
        return {
          data: { id: orderId, isOffline: true },
          isOffline: true,
          extraItems: items,
        };
      }
      return addItemsToOrder(String(orderId), { items });
    },
    onSuccess: (response: any, variables: any) => {
      const data = response.data || response;
      if (response.isOffline) {
        toast.info('💾 Productos guardados en el dispositivo (Sin conexión).');
        if (activeOrderId) {
          queryClient.setQueryData(['activeOrder', activeOrderId], (prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              items: [...(prev.items || []), ...(variables?.items || [])],
            };
          });
        }
      } else {
        toast.success('Productos añadidos');
        if (activeOrderId) {
          queryClient.setQueryData(['activeOrder', activeOrderId], data);
          queryClient.setQueryData(['activeOrder', String(activeOrderId)], data);
          queryClient.setQueryData(['activeOrder', Number(activeOrderId)], data);
        }
      }

      internalSetCart({});
    },
    onError: async (error: any, variables: any) => {
      const isNetworkErr = !error.response || error.code === 'ERR_NETWORK';
      if (isNetworkErr && variables) {
        await OfflineQueueService.pushOperation('ADD_ITEMS', { id: variables.orderId, items: variables.items });
        toast.warn('⚠️ Sin conexión. Productos guardados en memoria local.');
        internalSetCart({});
        return;
      }
      toast.error('Error al añadir productos');
    }
  });

  const removeItemMutation = useMutation({
    mutationFn: ({ orderId, itemId }: { orderId: string | number; itemId: string | number }) =>
      removeOrderItem(orderId, itemId),
    onSuccess: (data: any) => {
      toast.success('Producto quitado del pedido');
      if (activeOrderId) {
        queryClient.setQueryData(['activeOrder', activeOrderId], data);
        queryClient.setQueryData(['activeOrder', String(activeOrderId)], data);
        queryClient.setQueryData(['activeOrder', Number(activeOrderId)], data);
      }
      queryClient.invalidateQueries({ queryKey: ['ordersByTable', selectedTableRef.current?.id] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'No se pudo quitar el producto'),
  });

  const removeActiveOrderItem = (itemId: string | number) => {
    if (!activeOrderId) return;
    removeItemMutation.mutate({ orderId: activeOrderId, itemId });
  };

  const sendToKitchenMutation = useMutation({
    mutationFn: (orderId: string | number) => sendPendingToKitchen(String(orderId)),
    onSuccess: (response: any) => {
      const data = response.data || response;
      toast.success('Enviado a cocina');

      // Alimentar la caché al instante
      if (activeOrderId) {
        queryClient.setQueryData(['activeOrder', activeOrderId], data);
        queryClient.setQueryData(['activeOrder', String(activeOrderId)], data);
        queryClient.setQueryData(['activeOrder', Number(activeOrderId)], data);
      }
    }
  });

  const joinTablesMutation = useMutation({
    mutationFn: ({ primaryId, secondaryId }: { primaryId: string | number, secondaryId: string | number }) => joinTables(String(primaryId), String(secondaryId)),
    onSuccess: () => {
      toast.success('Mesas unidas');
      refetchTables();
    }
  });

  const unjoinTableMutation = useMutation({
    mutationFn: (id: string | number) => unjoinTable(String(id)),
    onSuccess: () => {
      toast.success('Mesa desvinculada');
      refetchTables();
    }
  });

  const splitOrderMutation = useMutation({
    mutationFn: ({ orderId, itemIds }: { orderId: string | number, itemIds: (string | number)[] }) => splitOrder(String(orderId), itemIds),
    onSuccess: (res: any) => {
      toast.success('Cuenta dividida con éxito');
      const responseData = res?.data || res;
      const original = responseData?.original;
      const newOrder = responseData?.new;

      // 1. Alimentar la caché de activeOrder de ambas cuentas al instante
      if (original?.id) {
        queryClient.setQueryData(['activeOrder', original.id], original);
        queryClient.setQueryData(['activeOrder', String(original.id)], original);
        queryClient.setQueryData(['activeOrder', Number(original.id)], original);
      }
      if (newOrder?.id) {
        queryClient.setQueryData(['activeOrder', newOrder.id], newOrder);
        queryClient.setQueryData(['activeOrder', String(newOrder.id)], newOrder);
        queryClient.setQueryData(['activeOrder', Number(newOrder.id)], newOrder);
      }

      // 2. Actualizar ordersByTable de la mesa de forma reactiva inmediata
      if (!isTakeawayMode && selectedTable?.id) {
        const tableKey = ['ordersByTable', selectedTable.id];
        const current = queryClient.getQueryData<any[]>(tableKey) ?? [];
        let updated = current.map((o: any) =>
          String(o.id) === String(original?.id) ? original : o
        );
        if (newOrder?.id && !updated.find((o: any) => String(o.id) === String(newOrder.id))) {
          updated = [...updated, newOrder];
        }
        queryClient.setQueryData(tableKey, updated);
      }

      // 3. Invalidar y refrescar para sincronizar con el servidor
      queryClient.invalidateQueries({ queryKey: ['ordersByTable', selectedTable?.id] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['posTables'] });
      refetchOrdersByTable();
      refetchActiveOrder();
      refetchTables();
    }
  });

  const transferOrderTableMutation = useMutation({
    mutationFn: ({ orderId, tableId }: { orderId: string | number, tableId: string | number }) => updateOrderTable(orderId, tableId),
    onSuccess: (data: any, variables: any) => {
      toast.success('Pedido transferido de mesa con éxito');
      const targetTable = tables?.find(t => String(t.id) === String(variables.tableId));
      if (targetTable) {
        setSelectedTableInternal(targetTable);
      }
      refetchTables();
      refetchActiveOrder();
    },
    onError: () => {
      toast.error('No se pudo transferir el pedido');
    }
  });

  // --- DERIVED DATA ---
  const activeOrderItems = useMemo(() => mapOrderItems(activeOrder), [activeOrder, mapOrderItems]);
  const activeOrderTotals = useMemo(() => {
    const items = activeOrderItems.map(i => ({ qty: i.quantity, unitPrice: i.unitPrice }));
    const discount = Number(activeOrder?.discount_amount) || 0;
    return computeOrderTotals(items, { discount });
  }, [activeOrderItems, activeOrder?.discount_amount]);
  const activeOrderTotal = activeOrderTotals.total;

  const hasActiveOrder = useMemo(() => {
    // Una orden está activa si no ha sido PAGADA ni CANCELADA.
    // Usamos payment_status para el estado de pago y status para cocina.
    const isPaid = activeOrder?.payment_status === 'PAID' || activeOrder?.paid === true;
    const isCancelled = activeOrder?.status?.toUpperCase() === 'CANCELLED';
    return !!activeOrder && !isPaid && !isCancelled;
  }, [activeOrder]);

  const sortedTableOrders = useMemo(() => {
    if (!ordersByTable) return [];
    return ordersByTable
      .filter(o => {
        const isPaid = o.payment_status === 'PAID' || o.paid === true;
        const isCancelled = (o.status || '').toUpperCase() === 'CANCELLED';
        return !isPaid && !isCancelled;
      })
      .sort((a,b) => {
        const seqA = a.orderSequence ?? (a as any).order_sequence ?? 0;
        const seqB = b.orderSequence ?? (b as any).order_sequence ?? 0;
        if (seqA !== seqB) return seqA - seqB;
        return Number(a.id || 0) - Number(b.id || 0);
      });
  }, [ordersByTable]);

  const activeOrderAccountIndex = useMemo(() => {
    if (!activeOrder || !sortedTableOrders.length) return null;
    const idx = sortedTableOrders.findIndex(o => String(o.id) === String(activeOrder.id));
    if (idx !== -1) {
      return idx + 1;
    }
    return 1;
  }, [activeOrder, sortedTableOrders]);

  const previousOrdersWithItems = useMemo(() => {
    // Si la mesa tiene múltiples cuentas abiertas, se gestionan mediante las pestañas de cuentas (Tabs)
    // para no duplicar la pantalla con tarjetas estáticas.
    if (sortedTableOrders.length > 1) {
      return [];
    }
    return sortedTableOrders
      .filter(o => o.id !== activeOrder?.id)
      .map(o => ({
        order: o,
        items: mapOrderItems(o),
        totals: computeOrderTotals(mapOrderItems(o).map(i => ({ qty: i.quantity, unitPrice: i.unitPrice }))),
        sequence: o.orderSequence ?? (o as any).order_sequence ?? 0
      }));
  }, [sortedTableOrders, activeOrder, mapOrderItems]);

  const highestKnownSequence = useMemo(() => {
    const sequences = (ordersByTable || []).map(o => o.orderSequence).filter(s => typeof s === 'number') as number[];
    return sequences.length > 0 ? Math.max(...sequences) : (ordersByTable?.length || 0);
  }, [ordersByTable]);

  const nextOrderSequence = highestKnownSequence + 1;
  const totals = useMemo(() => {
    if (hasActiveOrder) return activeOrderTotals;
    const items = Object.values(cart).map(c => ({
      qty: c.qty,
      unitPrice: (c.linePriceCalc?.final ?? 0) / c.qty || c.product.price + (c.serveMode === 'TAKEAWAY' ? (c.product.takeawayPrice || 0) : 0)
    }));
    return computeOrderTotals(items);
  }, [hasActiveOrder, activeOrderTotals, cart]);

  // --- AUTO-SELECT ACTIVE ORDER ---
  useEffect(() => {
    if (sortedTableOrders.length > 0) {
      // Si no hay orden activa, o la orden activa no pertenece a las órdenes de esta mesa,
      // seleccionamos la última. Si hay un activeOrderId seteado pero ordersByTable todavía
      // no lo tiene (caché stale justo después de crear), NO lo reseteamos — el siguiente
      // ciclo lo encontrará una vez que el caché se actualice.
      const foundInList = sortedTableOrders.find(o => String(o.id) === String(activeOrderId));
      if (!activeOrderId || !foundInList) {
        setActiveOrderId(sortedTableOrders[sortedTableOrders.length - 1].id);
      }
    } else if (ordersByTable !== undefined && sortedTableOrders.length === 0) {
      // ordersByTable ya cargó y no hay órdenes activas para esta mesa → limpiar
      // (la condición de carrera tras crear una orden está resuelta porque onSuccess
      // ya inserta la nueva orden en la caché de ordersByTable antes de setActiveOrderId)
      if (activeOrderId) setActiveOrderId(null);
    }
  }, [sortedTableOrders, activeOrderId, ordersByTable]);

  // --- ACTIONS ---
  const addProductToCart = useCallback((product: Product, flavors: any[] = [], opts: any = {}) => {
    const key = `${product.id}-${Date.now()}`;
    const newItem: CartItem = {
      product,
      qty: 1,
      flavors,
      additions: opts.additions || {},
      exceptions: opts.exceptions || [],
      serveMode: draftFulfillment === 'DINE_IN' ? 'MESA' : 'TAKEAWAY',
    };

    internalSetCart((prev: any) => ({ ...prev, [key]: newItem }));
    setPendingProductId(String(product.id));
    setTimeout(() => setPendingProductId(null), 600);
    toast.success(`✓ ${product.name} agregado`, { autoClose: 1500, hideProgressBar: true });
  }, [draftFulfillment, internalSetCart]);

  const addProductToActiveOrder = useCallback(async (product: Product, payload: any = {}) => {
    if (!activeOrderId) return;

    // Transformamos sabores si existen en el payload
    const formattedFlavors = safeArray(payload.flavors).map((f: any) => ({
      flavorId: f.id,
      qty: f.qty
    }));

    setPendingProductId(String(product.id));
    addItemsMutation.mutate({
      orderId: activeOrderId,
      items: [{
        product: product.id,
        quantity: 1,
        ...payload,
        flavors: formattedFlavors
      }]
    }, {
      onSettled: () => setTimeout(() => setPendingProductId(null), 600),
    });
    toast.success(`✓ ${product.name} agregado`, { autoClose: 1500, hideProgressBar: true });
  }, [activeOrderId, addItemsMutation]);

  const handleProductClick = useCallback((product: Product) => {
    // Registrar uso para la categoría "Frecuentes"
    trackProductUse(product.id);

    const needsConfig = (product.maxFlavors && product.maxFlavors > 0) ||
                       product.flavors?.enabled || product.additions?.enabled || product.exceptions?.enabled;

    if (needsConfig) {
      setFlavorContext({ product, target: hasActiveOrder ? 'order' : 'cart' });
    } else {
      if (hasActiveOrder) addProductToActiveOrder(product);
      else addProductToCart(product);
    }
  }, [hasActiveOrder, addProductToActiveOrder, addProductToCart]);

  const handleOrder = useCallback(() => {
    if (Object.keys(cart).length === 0) return;
    const items = Object.values(cart).map(item => ({
      product: item.product.id,
      quantity: item.qty,
      flavors: safeArray(item.flavors).map(f => ({
        flavorId: f.id,
        qty: f.qty
      })),
      additions: item.additions,
      exceptions: item.exceptions,
      serveMode: item.serveMode,
      notes: item.note || undefined,
    }));

    createOrderMutation.mutate({
      tableId: isTakeawayMode ? null : selectedTable?.id,
      destination: isTakeawayMode ? 'TAKEAWAY' : undefined,
      items,
      fulfillmentType: draftFulfillment,
      customerName: orderCustomer.customerName,
      customerPhone: orderCustomer.customerPhone,
      customerAddress: orderCustomer.customerAddress,
    });
  }, [cart, isTakeawayMode, selectedTable, draftFulfillment, orderCustomer, createOrderMutation]);

  // Construye la comanda a partir de los ítems de la orden (todos o solo pendientes)
  const buildComanda = useCallback((onlyPending: boolean): OrderTicketData | null => {
    const all = activeOrderItems;
    if (!all.length) return null;
    const pending = all.filter(i => ['PENDING', ''].includes((i.status || '').toUpperCase()));
    const source = onlyPending ? pending : all;
    if (!source.length) return null;

    const items = source.map((i: any) => ({
      qty: i.quantity || 1,
      name: i.name || i.product?.name || 'Producto',
      notes: i.notes || undefined,
      extras: [
        ...(i.serveMode === 'TAKEAWAY' || i.fulfillment === 'TAKEAWAY' || i.itemStatus === 'TAKEAWAY' || i.fulfillment_type_override === 'TAKEAWAY' || i.fulfillment_type_override === 'TAKEOUT' || i.fulfillmentTypeOverride === 'TAKEAWAY' || i.fulfillmentTypeOverride === 'TAKEOUT' ? ['(PARA LLEVAR)'] : []),
        ...((i.itemFlavors || i.item_flavors || []).map((f: any) =>
          `${(f.qty ?? 1) > 1 ? (f.qty + 'x ') : ''}${f.flavor?.name ?? f.name ?? ''}`.trim()).filter(Boolean)),
        ...(Array.isArray(i.formattedAdditions) ? i.formattedAdditions : []),
        ...(Array.isArray(i.exceptions) ? i.exceptions.map((e: any) => {
          const val = String(e);
          return val.toLowerCase().startsWith('sin ') ? val : `Sin ${val}`;
        }) : []),
      ],
    }));

    const isAddition = onlyPending && activeOrderItems.some(i => !['PENDING', ''].includes((i.status || '').toUpperCase()));
    const custName = activeOrder?.customerName || (activeOrder as any)?.customer_name || (isTakeawayMode ? orderCustomer.customerName : undefined);
    const custPhone = activeOrder?.customerPhone || (activeOrder as any)?.customer_phone || (isTakeawayMode ? orderCustomer.customerPhone : undefined);
    const custAddress = activeOrder?.customerAddress || (activeOrder as any)?.customer_address || (isTakeawayMode ? orderCustomer.customerAddress : undefined);

    let title = isTakeawayMode
      ? (custName ? `PARA LLEVAR: ${custName}` : 'PARA LLEVAR')
      : (selectedTable?.number != null ? `MESA ${selectedTable.number}` : (activeOrder?.table_id ? `MESA ${activeOrder.table_id}` : 'PEDIDO'));

    if (isAddition) {
      title = `${title} (ADICION)`;
    }

    return {
      orderId: activeOrderId ? String(activeOrderId) : undefined,
      title,
      orderNumber: activeOrder?.orderSequence ?? activeOrderId ?? undefined,
      time: new Date().toISOString(),
      customerName: custName || undefined,
      customerPhone: custPhone || undefined,
      customerAddress: custAddress || undefined,
      items,
      onlyPending,
      itemIds: source.map((i: any) => i.id).filter(Boolean),
    };
  }, [activeOrderItems, isTakeawayMode, selectedTable, activeOrder, activeOrderId, orderCustomer]);

  const hasPendingItems = useMemo(() => {
    return activeOrderItems.some(i => ['PENDING', ''].includes((i.status || '').toUpperCase()));
  }, [activeOrderItems]);

  const pendingItemsCount = useMemo(() => {
    return activeOrderItems.filter(i => ['PENDING', ''].includes((i.status || '').toUpperCase())).length;
  }, [activeOrderItems]);

  const handleSendPending = useCallback(() => {
    if (!activeOrderId) return;
    const comanda = buildComanda(true);
    sendToKitchenMutation.mutate(activeOrderId, {
      onSuccess: () => { if (comanda && comanda.items.length) setOrderTicketToPrint(comanda); },
    });
  }, [activeOrderId, buildComanda, sendToKitchenMutation]);

  // Botón manual: imprimir comanda de la orden actual
  const printOrderTicket = useCallback((forceAll: boolean = false) => {
    if (!forceAll && hasPendingItems) {
      handleSendPending();
      return;
    }
    const comanda = buildComanda(false);
    if (!comanda || !comanda.items.length) { toast.info('No hay productos en la orden'); return; }
    setOrderTicketToPrint(comanda);

    if (activeOrderId) {
      const nowIso = new Date().toISOString();
      queryClient.setQueryData(['activeOrder', activeOrderId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          comandaPrinted: true,
          comanda_printed: true,
          comandaPrintedAt: old.comandaPrintedAt || old.comanda_printed_at || nowIso,
          comanda_printed_at: old.comanda_printed_at || old.comandaPrintedAt || nowIso,
        };
      });
      if (selectedTableRef.current?.id) {
        queryClient.setQueryData(['ordersByTable', selectedTableRef.current.id], (old: any[]) => {
          if (!Array.isArray(old)) return old;
          return old.map(o => String(o.id) === String(activeOrderId) ? {
            ...o,
            comandaPrinted: true,
            comanda_printed: true,
            comandaPrintedAt: o.comandaPrintedAt || o.comanda_printed_at || nowIso,
            comanda_printed_at: o.comanda_printed_at || o.comandaPrintedAt || nowIso,
          } : o);
        });
      }
    }
  }, [hasPendingItems, handleSendPending, buildComanda, activeOrderId, queryClient]);

  const handleOrderFulfillmentChange = async (next: FulfillmentType) => {
    if (!activeOrder) return;
    await updateOrderFulfillment(activeOrder.id, next);
    refetchActiveOrder();
    toast.success('Entrega actualizada');
  };

  const handleActiveItemFulfillment = async (itemId: string, next: FulfillmentType | null) => {
    if (!activeOrder) return;
    await updateItemFulfillmentOverride(activeOrder.id, itemId, next);
    refetchActiveOrder();
  };

  const handleUpdateActiveItemNote = async (itemId: string, note: string) => {
    if (!activeOrder) return;
    await updateOrderItemNotes(activeOrder.id, itemId, note);
    refetchActiveOrder();
  };

  const handleCartItemServeMode = (key: string, next: ServeMode) => {
    internalSetCart((prev: any) => ({
      ...prev,
      [key]: { ...prev[key], serveMode: next }
    }));
  };

  // --- TRANSACTIONS ---
  const trans = usePosTransactions({
    isTakeawayMode, activeOrder, activeOrderTotal, activeOrderItems, activeOrderTotals,
    selected: selectedTable, tables, user, orderCustomer,
    cancelOrderMutation, unjoinTableMutation: unjoinTableMutation as any, updateTableMutation: {} as any,
    setCancelModalOpen: () => setShowCancelModal(true), setActiveOrderId, setCart: internalSetCart, refetchActiveOrder, refetchTables, setSelected: handleSetSelectedTable,
    sortedTableOrders, refetchOrdersByTable
  });

  const printPreReceipt = useCallback((targetOrder?: Order | null, targetItems?: any[]) => {
    const order = targetOrder ?? activeOrder;
    const items = targetItems ?? activeOrderItems;
    if (!order || !items || items.length === 0) {
      toast.info('No hay productos en la orden');
      return;
    }

    const ordTotals = targetOrder 
      ? computeOrderTotals(items.map((i: any) => ({ qty: i.quantity, unitPrice: i.unitPrice })))
      : (activeOrderTotals ?? { subtotal: activeOrderTotal, total: activeOrderTotal });

    const subtotal = ordTotals?.subtotal ?? activeOrderTotal;
    const total = ordTotals?.total ?? activeOrderTotal;
    const discountAmount = Number((order as any)?.discount_amount) || (subtotal > total ? subtotal - total : undefined);

    const preReceiptData = buildReceiptData({
      order,
      items: items.map((item: any) => ({
        name: item.name || item.product?.name || 'Producto',
        quantity: item.quantity ?? 0,
        unitPrice: Number(item.unitPrice ?? item.base_price ?? 0),
        total: Number(item.line_subtotal ?? (item.unitPrice ?? item.base_price ?? 0) * (item.quantity ?? 0)),
        notes: item.kitchenSummary || item.notes,
      })),
      totals: { subtotal, total },
      discountAmount,
      table: selectedTable,
      customerName: orderCustomer.customerName || order.customerName,
      paidAt: new Date().toISOString(),
      isPreReceipt: true,
    });

    trans.setReceiptToPrint(preReceiptData);
  }, [activeOrder, activeOrderItems, activeOrderTotals, activeOrderTotal, selectedTable, orderCustomer, trans.setReceiptToPrint]);

  const value: PosContextValue = {
    tables, products, categories, ordersByTable, activeOrder, loadingTables, loadingProducts,
    selectedTable, setSelectedTable: handleSetSelectedTable, activeCategory, setActiveCategory, searchTerm, setSearchTerm, isTakeawayMode,
    cart, setCart: internalSetCart, activeOrderId, setActiveOrderId, draftFulfillment, setDraftFulfillment, orderCustomer, setOrderCustomer,
    flavorContext, setFlavorContext, optionsKey, setOptionsKey, mobileTab, setMobileTab,
    pendingProductId,
    handleProductClick, handleOrder, handleSendPending, addProductToCart, addProductToActiveOrder,
    handleOrderFulfillmentChange, handleActiveItemFulfillment, handleUpdateActiveItemNote,
    handleCartItemServeMode, changeQty, removeCartItem,
    addItemsMutation, sendToKitchenMutation, splitOrderMutation, joinTablesMutation, unjoinTableMutation, createOrderMutation, cancelOrderMutation, transferOrderTableMutation,
    removeActiveOrderItem, removingItem: removeItemMutation.isPending,
    orderTicketToPrint, setOrderTicketToPrint, printOrderTicket, printPreReceipt,
    filteredProducts: (() => {
      const term = searchTerm.toLowerCase();
      const base = safeArray(products).filter(p => p.name.toLowerCase().includes(term));

      // Categoría especial: Frecuentes (ordenado por uso local)
      if (activeCategory === FREQUENT_CATEGORY_ID) {
        const freq = getFrequencyMap();
        return base
          .filter(p => (freq[String(p.id)] ?? 0) > 0)
          .sort((a, b) => (freq[String(b.id)] ?? 0) - (freq[String(a.id)] ?? 0));
      }

      if (!activeCategory) return base;
      return base.filter(p => {
        const catId = typeof p.category === 'string' ? p.category : p.category?.id;
        return catId === activeCategory;
      });
    })(),
    activeOrderItems, hasActiveOrder, orderLocked: activeOrder?.status !== 'WAITING', hasDraft: Object.keys(cart).length > 0,
    totals, highestKnownSequence, nextOrderSequence, activeOrderTotal, activeOrderTotals, previousOrdersWithItems,
    refetchActiveOrder,
    activeOrderIdMaybeLoading: !!activeOrderId && !activeOrderError,
    ...trans,
    buildDetailContentFromOrderItem: (item) => {
      const flavorsList = safeArray(item.itemFlavors || item.item_flavors || item.flavors);
      const additionsList = safeArray(item.formattedAdditions);
      const exceptionsList = safeArray(item.exceptions);

      return (
        <div className="flex flex-col text-xs text-muted mt-1 gap-0.5">
          {flavorsList.map((f: any, idx) => {
            const rawName = typeof f === 'string' ? f : (f.flavor?.name || f.name || f.flavor_name);
            const id = typeof f === 'object' ? (f.flavor_id ?? f.flavorId ?? f.id) : f;
            const resolvedName = rawName || allFlavors?.find(fl => String(fl.id) === String(id))?.name || (id ? `Sabor #${id}` : '');
            if (!resolvedName) return null;
            const qty = typeof f === 'object' && (f.qty ?? 1) > 1 ? `${f.qty}x ` : '';
            return <span key={`f-${idx}`}>• {qty}{resolvedName}</span>;
          })}
          {additionsList.map((a: any, idx: number) => (
            <span key={`a-${idx}`}>• + {String(a)}</span>
          ))}
          {exceptionsList.map((e: any, idx: number) => {
            const v = String(e.name || e.key || e).toLowerCase();
            return <span key={`e-${idx}`} className="text-red-500">• {v.startsWith('sin ') ? v : `Sin ${v}`}</span>;
          })}
          {item.notes && <span className="italic">Nota: {item.notes}</span>}
          {!flavorsList.length && !additionsList.length && !exceptionsList.length && !item.notes && (
            <span>{item.name || item.product?.name}</span>
          )}
        </div>
      );
    },
    buildDetailContentFromCartItem: (item) => {
      const flavorsList = safeArray(item.flavors);
      const additionsList = Object.entries(item.additions || {});
      const exceptionsList = safeArray(item.exceptions);

      return (
        <div className="flex flex-col text-xs text-muted mt-1 gap-0.5">
          {flavorsList.map((f: any, idx) => {
            const rawName = typeof f === 'string' ? f : (f.name || f.flavor?.name || f.flavor_name);
            const id = typeof f === 'object' ? (f.id ?? f.flavorId) : f;
            const resolvedName = rawName || allFlavors?.find(fl => String(fl.id) === String(id))?.name || (id ? `Sabor #${id}` : '');
            if (!resolvedName) return null;
            const qty = typeof f === 'object' && f.qty > 1 ? `${f.qty}x ` : '';
            return <span key={`f-${idx}`}>• {qty}{resolvedName}</span>;
          })}
          {additionsList.map(([key, qty]: [string, any], idx: number) => {
            const opt = (item.product?.additions as any)?.options?.find((o: any) => o.key === key);
            const additionName = opt?.name || key;
            return <span key={`a-${idx}`}>• + {qty > 1 ? `${qty}x ` : ''}{additionName}</span>;
          })}
          {exceptionsList.map((e: any, idx: number) => {
            const v = String(e.name || e.key || e).toLowerCase();
            return <span key={`e-${idx}`} className="text-red-500">• {v.startsWith('sin ') ? v : `Sin ${v}`}</span>;
          })}
          {item.note && <span className="italic">Nota: {item.note}</span>}
          {!flavorsList.length && !additionsList.length && !exceptionsList.length && !item.note && (
            <span>{item.product?.name || (item as any).name}</span>
          )}
        </div>
      );
    },
    sortedTableOrders,
    activeOrderAccountIndex,
    activeOrderSequence: activeOrderAccountIndex,
    hasFlavorErrors: false,
    hasPendingItems,
    sendToKitchenMutationIsPending: sendToKitchenMutation.isPending,
    pendingItemsCount,
    splittingOrder: splitOrderMutation.isPending,
    cancelOrderIsPending: cancelOrderMutation.isPending,
    createOrderIsPending: createOrderMutation.isPending,
  };

  return <PosContext.Provider value={value}>{children}</PosContext.Provider>;
}

export function usePos() {
  const context = useContext(PosContext);
  if (!context) throw new Error('usePos must be used within PosProvider');
  return context;
}
